<script lang="ts" setup>
import userStore from '@/store/UserStore'
import bus from '@/helper/Bus'
import Modals from '@/components/ModalHandler.vue'
import appStore from '@/store/AppStore'
import { BusEvent } from '@/helper/Types'
import { onUnmounted, ref } from 'vue'
import router from '@/helper/router'

const showLoadingOverlay = ref(false)
const loadingPipe = ref(0)

const COMPONENT_NAME = 'App'
const page = ref<HTMLElement | null>(null)

loadingTriggered(appStore.isLoading)

bus.$onC(COMPONENT_NAME, BusEvent.LOADING_STARTED, () => loadingTriggered(true))
bus.$onC(COMPONENT_NAME, BusEvent.LOADING_FINISHED, () => loadingTriggered(false))
bus.$onC(COMPONENT_NAME, BusEvent.USER_LOGOUT, () => router.push({ name: 'LoginFailPage' }).catch(() => ''))
bus.$onC(COMPONENT_NAME, BusEvent.SCROLL_TO_FIRST_ERROR, () => {
  const $firstError = page.value?.querySelector('.is--invalid')
  if ($firstError) {
    $firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
})

window.addEventListener('keyup', (e: KeyboardEvent) => {
  if (e.type !== 'keyup') {
    return
  }
  switch (e.key) {
    case 'Enter':
      bus.$emit(BusEvent.KEYPRESSED_ENTER, e)
      break
    case 'Escape':
      bus.$emit(BusEvent.KEYPRESSED_ESC, e)
      break
    case 'Tab':
      if (page.value) {
        page.value.classList.remove('no--outline')
        page.value.classList.add('has--outline')
      }
      break
  }
})

window.addEventListener('mousedown', () => {
  if (!page.value) {
    return
  }
  page.value.classList.add('no--outline')
  page.value.classList.remove('has--outline')
})

window.addEventListener('resize', e => {
  bus.$emit(BusEvent.WINDOW_RESIZE, e)
})

function loadingTriggered(isLoading: boolean): void {
  if (isLoading) {
    loadingPipe.value++
    if (loadingPipe.value) {
      setTimeout(() => {
        if (loadingPipe.value) {
          showLoadingOverlay.value = true
        }
      }, 1000)
    }
  } else {
    if (loadingPipe.value > 0) {
      loadingPipe.value--
    }
    showLoadingOverlay.value = !!loadingPipe.value
  }
}

onUnmounted((): void => {
  bus.$offC(COMPONENT_NAME)
})
</script>

<template>
  <div ref="page" :class="userStore.themeClass" class="page no--outline">
    <router-view />
    <transition name="fade">
      <div v-if="showLoadingOverlay" class="loading-overlay">
        <div class="loading-overlay__icon">
          <i class="ic-sync2" />
        </div>
      </div>
    </transition>
    <Modals />
  </div>
</template>

<style lang="scss">
@import '@/assets/style/style.scss';

.page {
  position: relative;
  min-height: 100vh;
  padding-bottom: rem-calc(64 + $footerHeight);
  background: $bgColor;
  overflow: hidden;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  padding-left: calc(100vw - 100%); //Fixing scroll bar jump issue

  &__icon {
    width: rem-calc(72);
    height: rem-calc(72);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    @include componentShadow();
    @include themify($themes) {
      background-color: themed('primaryColor');
    }

    i[class^='ic-'],
    i[class*=' ic-'] {
      color: #fff;

      &:before {
        animation: spin 1.3s infinite cubic-bezier(0.5, 0.1, 0.5, 0.9);
        font-size: rem-calc(42);
        line-height: rem-calc(42);
      }
    }
  }
}
</style>
